/* ============================================================================
   Raman Rasa — Revamp interactions (rr-theme.js)
   Vanilla JS, defer-loaded. Progressive enhancement only:
   - Scroll-spy for the sticky section nav (.rr-subnav)
   - Smooth-scroll with sticky-header offset
   - Reveal-on-scroll (.rr-reveal -> .is-visible)
   - Header height measurement -> --rr-header-h / --rr-subnav-h CSS vars
   - Mobile nav toggle hook ([data-rr-nav-toggle] / [data-rr-nav])
   ============================================================================ */
(function () {
  'use strict';

  var root = document.documentElement;
  document.body && document.body.classList.remove('rr-no-js');

  /* --------------------------------------------------------------------------
     Measure sticky header + subnav heights into CSS vars (used for offsets)
     -------------------------------------------------------------------------- */
  function measureChrome() {
    var header =
      document.querySelector('.rr-revamp .site-header') ||
      document.querySelector('.rr-revamp header[class*="header"]') ||
      document.querySelector('.shopify-section-header') ||
      document.querySelector('#shopify-section-header');
    var subnav = document.querySelector('.rr-subnav');

    var headerH = header ? Math.round(header.getBoundingClientRect().height) : 72;
    var subnavH = subnav ? Math.round(subnav.getBoundingClientRect().height) : 0;

    root.style.setProperty('--rr-header-h', headerH + 'px');
    root.style.setProperty('--rr-subnav-h', subnavH + 'px');
    return { headerH: headerH, subnavH: subnavH };
  }

  /* --------------------------------------------------------------------------
     Smooth-scroll for in-page anchor links (subnav + any [data-rr-scroll])
     -------------------------------------------------------------------------- */
  function offsetTop(el) {
    var chrome = measureChrome();
    var rectTop = el.getBoundingClientRect().top + window.pageYOffset;
    return rectTop - (chrome.headerH + chrome.subnavH + 12);
  }

  function smoothScrollTo(hash) {
    if (!hash || hash.charAt(0) !== '#' || hash.length < 2) return false;
    var target;
    try { target = document.querySelector(hash); } catch (e) { return false; }
    if (!target) return false;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: offsetTop(target),
      behavior: reduce ? 'auto' : 'smooth'
    });
    return true;
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest(
      '.rr-subnav__link[href^="#"], [data-rr-scroll][href^="#"]'
    );
    if (!link) return;
    var hash = link.getAttribute('href');
    if (smoothScrollTo(hash)) {
      e.preventDefault();
      if (history.replaceState) history.replaceState(null, '', hash);
    }
  });

  /* --------------------------------------------------------------------------
     Scroll-spy: highlight the active subnav link for the section in view
     -------------------------------------------------------------------------- */
  function initScrollSpy() {
    var subnav = document.querySelector('.rr-subnav');
    if (!subnav || !('IntersectionObserver' in window)) return;

    var links = Array.prototype.slice.call(
      subnav.querySelectorAll('.rr-subnav__link[href^="#"]')
    );
    if (!links.length) return;

    var map = {}; // id -> link
    var targets = [];
    links.forEach(function (link) {
      var id = link.getAttribute('href').slice(1);
      var section = document.getElementById(id);
      if (section) {
        map[id] = link;
        targets.push(section);
      }
    });
    if (!targets.length) return;

    function setActive(id) {
      links.forEach(function (l) {
        l.classList.toggle(
          'is-active',
          l.getAttribute('href') === '#' + id
        );
      });
    }

    var visible = {};
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          visible[entry.target.id] = entry.isIntersecting
            ? entry.intersectionRatio
            : 0;
        });
        // pick the most-visible section
        var bestId = null, best = 0;
        targets.forEach(function (t) {
          var r = visible[t.id] || 0;
          if (r > best) { best = r; bestId = t.id; }
        });
        if (bestId) setActive(bestId);
      },
      {
        rootMargin: '-45% 0px -45% 0px',
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1]
      }
    );
    targets.forEach(function (t) { observer.observe(t); });
  }

  /* --------------------------------------------------------------------------
     Reveal-on-scroll
     -------------------------------------------------------------------------- */
  function initReveal() {
    var els = Array.prototype.slice.call(
      document.querySelectorAll('.rr-revamp .rr-reveal:not([data-rr-reveal-bound])')
    );
    if (!els.length) return;

    /* Theme editor: section re-renders swap in fresh DOM on every settings
       change, and editors expect to see content immediately rather than
       waiting to scroll it into view — skip the hide/observe dance entirely. */
    if (window.Shopify && window.Shopify.designMode) {
      els.forEach(function (el) {
        el.setAttribute('data-rr-reveal-bound', '');
        el.classList.add('is-visible');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.setAttribute('data-rr-reveal-bound', '');
        el.classList.add('is-visible');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );
    els.forEach(function (el) {
      el.setAttribute('data-rr-reveal-bound', '');
      io.observe(el);
    });
  }

  /* --------------------------------------------------------------------------
     Mobile nav toggle (optional hook for redesigned header)
     -------------------------------------------------------------------------- */
  function initNavToggle() {
    var toggles = document.querySelectorAll('[data-rr-nav-toggle]');
    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var sel = btn.getAttribute('data-rr-nav-toggle');
        var panel = sel ? document.querySelector(sel) : null;
        var open = btn.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (panel) {
          panel.classList.toggle('is-open', open);
          document.body.classList.toggle('rr-nav-locked', open);
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     Boot
     -------------------------------------------------------------------------- */
  function init() {
    measureChrome();
    initScrollSpy();
    initReveal();
    initNavToggle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(measureChrome, 150);
  });
  window.addEventListener('load', measureChrome);

  /* --------------------------------------------------------------------------
     Theme editor: re-bind to freshly swapped-in section/block DOM.
     Shopify replaces the section's markup wholesale on every settings change
     (image picker, text edits, etc.) without a full page reload, so anything
     bound at initial DOMContentLoaded — the reveal observer in particular —
     needs to re-attach or the new nodes stay in their initial (hidden) state.
     -------------------------------------------------------------------------- */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:block:select', init);
})();
